import { MapBuilder, px, type Decor, type MapDef } from '../mapdef';

/**
 * School interior. North wing: counseling room, class 8-3, library.
 * A corridor runs east–west. South wing: art & music room, lobby (exit),
 * cafeteria and restrooms.
 */
const b = new MapBuilder(58, 30, ' ');
b.room(0, 0, 15, 13, '.'); // counseling room
b.room(14, 0, 16, 13, ','); // class 8-3
b.room(29, 0, 15, 13, ';'); // library
b.room(0, 12, 58, 6, ','); // corridor
b.room(43, 0, 15, 13, ','); // nurse's office
b.room(43, 17, 15, 13, '.'); // music room
b.room(0, 17, 16, 13, '.'); // art & music
b.room(15, 17, 8, 13, ','); // lobby
b.room(22, 17, 15, 13, 'k'); // cafeteria
b.room(36, 17, 8, 13, 'b'); // restrooms
for (const x of [6, 7, 21, 22, 35, 36, 49, 50]) b.set(x, 12, 'd');
for (const x of [7, 8, 17, 18, 19, 20, 29, 30, 37, 38, 49, 50]) b.set(x, 17, 'd');
b.fill(18, 29, 2, 1, 'D');
b.fill(4, 6, 6, 4, ':'); // counseling rug

const at = (tx: number, ty: number) => ({ x: px(tx), y: px(ty) });
const box = (tx: number, ty: number, tw: number, h = 10): Decor['solid'] => ({ x: px(tx) + 1, y: px(ty) - h, w: px(tw) - 2, h });

const decor: Decor[] = [
  // Counseling room
  { kind: 'window', x: px(5), y: 0, w: px(4), wall: true },
  { kind: 'clock', x: px(10), y: 2, wall: true },
  { kind: 'poster', x: px(3) + 4, y: 2, wall: true },
  { kind: 'armchairs', ...at(7, 8), solid: box(5, 8, 4, 8) },
  { kind: 'teaTable', ...at(7, 9), solid: box(6, 9, 2, 6) },
  { kind: 'pottedPlant', x: px(13) + 8, y: px(11) + 12, solid: { x: px(13) + 2, y: px(11) + 6, w: 12, h: 6 } },
  // Class 8-3
  { kind: 'blackboard', x: px(17), y: 1, w: px(9), wall: true },
  { kind: 'window', x: px(27), y: 0, w: px(2), wall: true },
  { kind: 'teacherDesk', ...at(21.5, 4), solid: box(19, 4, 5, 10) },
  ...[16, 19, 22, 25].flatMap((x) =>
    [7, 9, 11].filter((y) => !(x === 25 && y === 7) && !(x === 16 && y === 11)).map((y): Decor => ({ kind: 'studentDesk', ...at(x + 1, y), solid: box(x, y, 2, 8) })),
  ),
  // Library
  { kind: 'window', x: px(31), y: 0, w: px(3), wall: true },
  { kind: 'window', x: px(38), y: 0, w: px(3), wall: true },
  { kind: 'bookshelf', ...at(32, 5), w: px(4), solid: box(30, 5, 4, 10) },
  { kind: 'bookshelf', ...at(37, 5), w: px(4), solid: box(35, 5, 4, 10) },
  { kind: 'bookshelf', ...at(32, 8), w: px(4), solid: box(30, 8, 4, 10) },
  { kind: 'readingTable', ...at(37, 9), solid: box(35, 9, 4, 10) },
  { kind: 'counterTill', ...at(41, 11), w: px(3), solid: box(39.5, 11, 3, 10) },
  // Corridor
  { kind: 'lockers', ...at(28.5, 13), w: px(5), solid: box(26, 13, 5, 8) },
  { kind: 'lockers', ...at(16.5, 13), w: px(3), solid: box(15, 13, 3, 8) },
  { kind: 'mapBoard', x: px(2), y: px(12) + 2, wall: true },
  { kind: 'trashBin', ...at(33, 13), solid: { x: px(33) - 5, y: px(13) - 4, w: 10, h: 4 } },
  { kind: 'pottedPlant', x: px(24) + 8, y: px(13) + 12, solid: { x: px(24) + 2, y: px(13) + 6, w: 12, h: 6 } },
  // Art & music room
  { kind: 'painting', x: px(2), y: px(17) + 2, wall: true },
  { kind: 'painting', x: px(10), y: px(17) + 2, wall: true, variant: 1 },
  { kind: 'easelDeco', x: px(8) + 8, y: px(21), solid: box(8, 21, 1, 6) },
  { kind: 'easelDeco', x: px(12) + 8, y: px(21), solid: box(12, 21, 1, 6), variant: 1 },
  { kind: 'paintShelf', x: px(2) + 8, y: px(27), w: px(3), solid: box(1, 27, 3, 10) },
  // Nurse's office
  { kind: 'window', x: px(47), y: 0, w: px(4), wall: true },
  { kind: 'nurseBed', x: px(45) + 8, y: px(5), solid: box(44.5, 5, 2, 24) },
  { kind: 'nurseBed', x: px(55) + 8, y: px(5), solid: box(54.5, 5, 2, 24) },
  { kind: 'medCabinet', x: px(52) + 8, y: px(2), w: px(2), solid: box(51.5, 2, 2, 10) },
  { kind: 'curtainScreen', x: px(48) + 8, y: px(6), solid: box(48, 6, 1, 6) },
  { kind: 'poster', x: px(44) + 4, y: 2, wall: true, variant: 1 },
  // Music room
  { kind: 'drumKit', ...at(55, 26), solid: box(54, 26, 2, 10) },
  { kind: 'musicStands', x: px(48) + 8, y: px(25), solid: box(47, 25, 3, 6) },
  { kind: 'xylophone', x: px(45) + 8, y: px(27), solid: box(44.5, 27, 2, 8) },
  { kind: 'painting', x: px(52), y: px(17) + 2, wall: true, variant: 2 },
  // Lobby
  { kind: 'shoeLockers', ...at(16.5, 20), w: px(1), solid: box(16, 20, 1, 12) },
  { kind: 'shoeLockers', ...at(21.5, 20), w: px(1), solid: box(21, 20, 1, 12) },
  { kind: 'pottedPlant', x: px(16) + 8, y: px(28) + 12, solid: { x: px(16) + 2, y: px(28) + 6, w: 12, h: 6 } },
  { kind: 'pottedPlant', x: px(21) + 8, y: px(28) + 12, solid: { x: px(21) + 2, y: px(28) + 6, w: 12, h: 6 } },
  // Cafeteria
  ...[24, 28, 32].flatMap((x) => [23, 26].map((y): Decor => ({ kind: 'cafeTable', ...at(x + 1, y), solid: box(x, y, 2, 10) }))),
  // Restrooms
  { kind: 'sinkRow', ...at(39.5, 28), w: px(4), solid: box(37.5, 28, 4, 8) },
  // Classroom package: "!" bubbles over classmates who need a friend (drawn only while their case is live).
  { kind: 'cls_alert', x: px(23), y: px(14) + 2, variant: 0 },
  { kind: 'cls_alert', x: px(13), y: px(15) + 4, variant: 1 },
  { kind: 'cls_alert', x: px(27) + 10, y: px(11) + 4, variant: 2 },
];

export const school: MapDef = {
  id: 'school',
  w: b.w,
  h: b.h,
  rows: b.rows(),
  indoor: true,
  theme: { wallFace: '#efe3c8', wallLight: '#f8f0dc', wainscot: '#7c9a7a', wallTop: '#3b2a1e' },
  doors: [{ tiles: [[18, 29], [19, 29]], to: 'town', arrive: { x: px(28), y: px(8), facing: 'down' } }],
  decor,
  areas: [
    { key: 'counsel', rect: { x: 0, y: 0, w: px(15), h: px(12) } },
    { key: 'classroom', rect: { x: px(14), y: 0, w: px(15), h: px(12) } },
    { key: 'library', rect: { x: px(29), y: 0, w: px(15), h: px(12) } },
    { key: 'corridor', rect: { x: 0, y: px(12), w: px(58), h: px(5) } },
    { key: 'artRoom', rect: { x: 0, y: px(17), w: px(15), h: px(13) } },
    { key: 'lobby', rect: { x: px(15), y: px(17), w: px(7), h: px(13) } },
    { key: 'cafeteria', rect: { x: px(22), y: px(17), w: px(14), h: px(13) } },
    { key: 'restroom', rect: { x: px(36), y: px(17), w: px(7), h: px(13) } },
    { key: 'nurse', rect: { x: px(43), y: 0, w: px(15), h: px(12) } },
    { key: 'music', rect: { x: px(43), y: px(17), w: px(15), h: px(13) } },
  ],
  lights: [
    { x: px(7), y: px(6), r: 110, color: '255,236,190' },
    { x: px(22), y: px(6), r: 110, color: '255,240,210' },
    { x: px(36), y: px(6), r: 110, color: '255,236,190' },
    ...[6, 16, 26, 36].map((x) => ({ x: px(x), y: px(14), r: 70, color: '255,240,210' })),
    { x: px(8), y: px(23), r: 100, color: '255,236,190' },
    { x: px(19), y: px(23), r: 80, color: '255,236,190' },
    { x: px(29), y: px(23), r: 110, color: '255,236,190' },
    { x: px(40), y: px(23), r: 70, color: '240,250,255' },
    { x: px(50), y: px(6), r: 100, color: '240,250,255' },
    { x: px(50), y: px(23), r: 100, color: '255,236,190' },
  ],
};
